/**
 * The workspace header (WALLIYO-0132).
 *
 * The console does not hide rows per person: it becomes that department's
 * console, and this is where it says so. "Walliyo Console — Human Resources",
 * and the name is the control that changes it.
 *
 * Somebody holding one workspace — most staff — gets a plain heading with
 * nothing to press. A switcher offering one choice is furniture.
 */

import type { DepartmentId } from "@wallyo/contracts/workspaces";
import { useEffect, useRef, useState } from "react";

import { t } from "../i18n";
import { profileHref, workspaceBlurb, workspaceLabel } from "../navigation/screens";
import { BrandMark } from "./BrandMark";
import { Chevron, WorkspaceMark } from "./workspace-icons";

/** "Fatoumata Bah" → "FB". Two letters, because the circle holds two. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase();
}

export function WorkspaceHeader({
  current,
  available,
  onSwitch,
  who,
  onProfile,
}: {
  current: DepartmentId | null;
  available: readonly DepartmentId[];
  onSwitch: (id: DepartmentId) => void;
  /** The signed-in person's name. Their avatar sits at the end of this bar. */
  who: string;
  /** True when the profile is the open route, so the avatar reads as current. */
  onProfile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  // A menu that stays open once you have looked away is a menu in the way.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <header className="ws-head">
      {/* The mark, not the letter W in a box it used to stand in for. */}
      <BrandMark size={30} tone="onDark" />
      <span className="ws-wordmark">{t("brand.name")}</span>

      {current === null ? null : (
        <>
          <span className="ws-rule" aria-hidden="true" />
          {available.length < 2 ? (
            <span className="ws-only">
              <WorkspaceMark id={current} />
              <h1 className="ws-name">{workspaceLabel(current)}</h1>
            </span>
          ) : (
            <div className="ws-switch" ref={box}>
              <button
                type="button"
                className={open ? "ws-pick on" : "ws-pick"}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((was) => !was)}
              >
                <WorkspaceMark id={current} />
                <span className="ws-name">{workspaceLabel(current)}</span>
                <span className="ws-chevron">
                  <Chevron up={open} />
                </span>
              </button>
              {open ? (
                <div className="ws-menu" role="menu">
                  <p className="ws-menu-head">{t("workspace.yours")}</p>
                  {available.map((id) => (
                    <button
                      key={id}
                      type="button"
                      role="menuitem"
                      className={id === current ? "ws-menu-item on" : "ws-menu-item"}
                      onClick={() => {
                        setOpen(false);
                        onSwitch(id);
                      }}
                    >
                      <span className="ws-menu-name">
                        <WorkspaceMark id={id} size={15} />
                        {workspaceLabel(id)}
                      </span>
                      <span className="ws-menu-blurb">{workspaceBlurb(id)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

      {/* The person, at the end of the bar. They are not a department, so they
          do not belong in the department's sidebar — the design puts them here,
          where they stay put whichever workspace is showing. */}
      <span className="ws-spacer" />
      <a
        className={`ws-me${onProfile ? " on" : ""}`}
        href={profileHref()}
        aria-current={onProfile ? "page" : undefined}
        aria-label={t("profile.open")}
        title={who}
      >
        <span aria-hidden="true">{initials(who)}</span>
      </a>
    </header>
  );
}
